import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { EarnedBadge } from "@/components/performance/portal/rewards/rewardTypes";

export type AwardType = "week" | "month" | "year";

export interface AwardHistoryItem {
  id: string;
  award_type: AwardType;
  period_label: string;
  period_start: string;
  period_end: string;
  awarded_at: string;
  badges: EarnedBadge[];
}

export interface OrgAwardHistoryItem extends AwardHistoryItem {
  staff_id: string;
  staff_name: string;
  staff_avatar: string | null;
  role: string | null;
  department: string | null;
}

export interface AwardHistoryResponse {
  count: number;
  total_pages: number;
  current_page: number;
  results: AwardHistoryItem[];
}

export interface OrgAwardHistoryResponse {
  count: number;
  total_pages: number;
  current_page: number;
  results: OrgAwardHistoryItem[];
}

const PAGE_SIZE = 20;

interface RecognitionHistoryState {
  // self / per-staff view
  items: AwardHistoryItem[];
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  activeAwardType: AwardType;
  clientFetched: boolean;
  // org-wide view
  orgItems: OrgAwardHistoryItem[];
  orgCount: number;
  orgTotalPages: number;
  orgCurrentPage: number;
  orgPageSize: number;
  orgClientFetched: boolean;
}

const initialState: RecognitionHistoryState = {
  items: [],
  count: 0,
  total_pages: 1,
  current_page: 1,
  page_size: PAGE_SIZE,
  activeAwardType: "month",
  clientFetched: false,
  orgItems: [],
  orgCount: 0,
  orgTotalPages: 1,
  orgCurrentPage: 1,
  orgPageSize: PAGE_SIZE,
  orgClientFetched: false,
};

const recognitionHistorySlice = createSlice({
  name: "recognitionHistory",
  initialState,
  reducers: {
    setAwardHistoryFromSSR(state, action: PayloadAction<AwardHistoryResponse>) {
      if (state.clientFetched) return;
      state.items = action.payload.results;
      state.count = action.payload.count;
      state.total_pages = action.payload.total_pages;
      state.current_page = action.payload.current_page;
    },
    setAwardHistory(state, action: PayloadAction<AwardHistoryResponse>) {
      state.items = action.payload.results;
      state.count = action.payload.count;
      state.total_pages = action.payload.total_pages;
      state.current_page = action.payload.current_page;
      state.clientFetched = true;
    },
    setAwardType(state, action: PayloadAction<AwardType>) {
      state.activeAwardType = action.payload;
      state.current_page = 1;
      state.clientFetched = false;
      state.orgCurrentPage = 1;
      state.orgClientFetched = false;
    },
    setAwardHistoryPage(state, action: PayloadAction<number>) {
      state.current_page = action.payload;
      state.clientFetched = false;
    },
    setAwardHistoryPageSize(state, action: PayloadAction<number>) {
      state.page_size = action.payload;
      state.current_page = 1;
      state.clientFetched = false;
    },
    setOrgAwardHistoryFromSSR(
      state,
      action: PayloadAction<OrgAwardHistoryResponse>,
    ) {
      if (state.orgClientFetched) return;
      state.orgItems = action.payload.results;
      state.orgCount = action.payload.count;
      state.orgTotalPages = action.payload.total_pages;
      state.orgCurrentPage = action.payload.current_page;
    },
    setOrgAwardHistory(state, action: PayloadAction<OrgAwardHistoryResponse>) {
      state.orgItems = action.payload.results;
      state.orgCount = action.payload.count;
      state.orgTotalPages = action.payload.total_pages;
      state.orgCurrentPage = action.payload.current_page;
      state.orgClientFetched = true;
    },
    setOrgAwardHistoryPage(state, action: PayloadAction<number>) {
      state.orgCurrentPage = action.payload;
      state.orgClientFetched = false;
    },
    setOrgAwardHistoryPageSize(state, action: PayloadAction<number>) {
      state.orgPageSize = action.payload;
      state.orgCurrentPage = 1;
      state.orgClientFetched = false;
    },
  },
});

export const {
  setAwardHistoryFromSSR,
  setAwardHistory,
  setAwardType,
  setAwardHistoryPage,
  setAwardHistoryPageSize,
  setOrgAwardHistoryFromSSR,
  setOrgAwardHistory,
  setOrgAwardHistoryPage,
  setOrgAwardHistoryPageSize,
} = recognitionHistorySlice.actions;

export default recognitionHistorySlice.reducer;
